import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, unref as _unref, isRef as _isRef, vModelText as _vModelText, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_2 = ["value"]
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = { class: "flex items-stretch gap-2" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "flex flex-row gap-1" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = { class: "mb-4" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = { class: "mb-4" }
const _hoisted_12 = { class: "flex justify-between w-full" }
const _hoisted_13 = { class: "flex items-center gap-2" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = ["disabled"]

import { ref, watch, Ref, computed } from "vue";
import { useChallengeForm } from "@/composables/tasks/challenge/useChallengeForm";
import { useRecord } from "@/composables/tasks/challenge/useChallengeRecords";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { ChallengeProgressEntry, ChallengeConfiguration } from "@/types/v2/task/challenge/configuration.types";

import ModalBox from "@/components/v2/Modal/ModalBox.vue";

// ===================================================================
// Props
// ===================================================================
interface Props {
    configuration: ChallengeConfiguration;
    show: boolean;
    onClose?: () => void;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ChallengeModal',
  props: {
    configuration: {},
    show: { type: Boolean },
    onClose: { type: Function }
  },
  setup(__props: any) {

const props = __props;

// ===================================================================
// STATE - Modal
// ===================================================================
const showModal: Ref<boolean> = ref(props.show);

// ===================================================================
// COMPOSABLES
// ===================================================================
const {
    challenged,
    weight,
    comment,
    challengeDuration,
    successMessage,

    isWeightDisabled,
    isChallengedDisabled,
    canSave,

    incrementChallenged,
    decrementChallenged,
    resetForm
} = useChallengeForm();

const { createChallengeRecord } = useRecord();
const { getEquipmentConfigurations } = useEquipment();

// ===================================================================
// Administration Equipment
// ===================================================================
// Get administration equipment configuration
const administrationEquipment = computed(() => {
    return getEquipmentConfigurations.value.find(config =>
        config.equipmentType.name.toLowerCase() === "administration equipment"
    );
});

// Get administration equipment options
const administrationEquipmentOptions = computed(() => {
    if (administrationEquipment.value?.equipmentType?.configOptions) {
        const typeOption = administrationEquipment.value.equipmentType.configOptions.find(opt => opt.name === "type");
        return typeOption?.options || [];
    }
    return [];
});

// Update the admin equipment type
const updateAdminEquipmentType = (type: string) => {
    if (administrationEquipment.value) {
        administrationEquipment.value.values.type = type;
        administrationEquipment.value.updated_at = new Date().toISOString();
    }
};

// ===================================================================
// ACTIONS - Modal
// ===================================================================
const closeModal = () => {
    if (props.onClose) {
        props.onClose();
    }
};

// ===================================================================
// ACTIONS - Save Challenge Record
// ===================================================================
const handleSaveChallengeData = (keepModalOpen = false) => {
    if (!canSave.value || !props.configuration) return;

    createChallengeRecord(props.configuration.key, {
        challenged: challenged.value,
        weight: weight.value,
        comment: comment.value,
        challengeDuration: props.configuration.challengeDuration || challengeDuration.value,
        // Include the administration equipment type
        administrationEquipment: administrationEquipment.value?.values?.type || null
    } as ChallengeProgressEntry);

    resetForm();

    if (!keepModalOpen) {
        return closeModal();
    }
};

// ===================================================================
// Watchers
// ===================================================================
watch(
    () => props.show,
    (newVal) => {
        showModal.value = newVal;
    }
);

return (_ctx: any,_cache: any) => {
  return (showModal.value)
    ? (_openBlock(), _createBlock(ModalBox, {
        key: 0,
        onClose: closeModal
      }, {
        header: _withCtx(() => _cache[9] || (_cache[9] = [
          _createElementVNode("h1", { class: "text-xl font-semibold" }, "Record Challenge Administration", -1)
        ])),
        main: _withCtx(() => [
          (administrationEquipmentOptions.value.length > 0 && administrationEquipment.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _cache[10] || (_cache[10] = _createElementVNode("label", { class: "block text-gray-700 mb-2" }, "Administration Equipment:", -1)),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((administrationEquipment.value.values.type) = $event)),
                  onChange: _cache[1] || (_cache[1] = ($event: any) => (updateAdminEquipmentType(administrationEquipment.value.values.type))),
                  class: "form-select bg-white p-2.5 block w-full border border-gray-300 rounded shadow-sm"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(administrationEquipmentOptions.value, (option) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: option,
                      value: option
                    }, _toDisplayString(option), 9, _hoisted_2))
                  }), 128))
                ], 544), [
                  [_vModelSelect, administrationEquipment.value.values.type]
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_3, [
            _cache[11] || (_cache[11] = _createElementVNode("label", { class: "block text-gray-700 mb-2" }, "Administer Challenge:", -1)),
            _createElementVNode("div", _hoisted_4, [
              _withDirectives(_createElementVNode("input", {
                type: "number",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(challenged) ? (challenged).value = $event : null)),
                disabled: _unref(isChallengedDisabled),
                min: "1",
                pattern: "\\d*",
                class: "form-input p-2 block flex-1 border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:cursor-not-allowed"
              }, null, 8, _hoisted_5), [
                [
                  _vModelText,
                  _unref(challenged),
                  void 0,
                  { number: true }
                ]
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("button", {
                  onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(incrementChallenged) && _unref(incrementChallenged)(...args))),
                  disabled: _unref(isChallengedDisabled),
                  class: "p-1 w-10 text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold"
                }, " + ", 8, _hoisted_7),
                _createElementVNode("button", {
                  onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(decrementChallenged) && _unref(decrementChallenged)(...args))),
                  disabled: _unref(isChallengedDisabled) || _unref(challenged) <= 1,
                  class: "p-1 w-10 text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold"
                }, " − ", 8, _hoisted_8)
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _cache[12] || (_cache[12] = _createElementVNode("label", { class: "block text-gray-700 mb-2" }, "Weight:", -1)),
            _withDirectives(_createElementVNode("input", {
              type: "number",
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(weight) ? (weight).value = $event : null)),
              disabled: _unref(isWeightDisabled) === true,
              min: "0",
              class: "form-input p-2 block w-full border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            }, null, 8, _hoisted_10), [
              [_vModelText, _unref(weight)]
            ])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _cache[13] || (_cache[13] = _createElementVNode("label", { class: "block text-gray-700 mb-2" }, "Comment:", -1)),
            _withDirectives(_createElementVNode("textarea", {
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(comment) ? (comment).value = $event : null)),
              placeholder: "Leave a comment",
              class: "form-input p-2 block w-full border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px]"
            }, null, 512), [
              [_vModelText, _unref(comment)]
            ])
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("button", {
                onClick: _cache[7] || (_cache[7] = ($event: any) => (handleSaveChallengeData(false))),
                type: "button",
                disabled: !_unref(canSave),
                class: "text-white bg-primary hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
              }, " Save and close ", 8, _hoisted_14),
              _createElementVNode("button", {
                onClick: _cache[8] || (_cache[8] = ($event: any) => (handleSaveChallengeData(true))),
                type: "button",
                disabled: !_unref(canSave) || _unref(successMessage) !== '',
                class: "text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
              }, _toDisplayString(_unref(successMessage) !== "" ? "Save " + _unref(successMessage) : "Save"), 9, _hoisted_15)
            ]),
            _createElementVNode("button", {
              onClick: closeModal,
              type: "button",
              class: "text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
            }, " Back ")
          ])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})